import { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from 'react';
import { FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import gallery from '../data/gallery.json';
import './Gallery.css';

function getColumnCount(width) {
  if (width >= 1200) return 5;
  if (width >= 900) return 4;
  if (width >= 640) return 3;
  return 2;
}

const GAP = 12;

// First photo stays pinned top-left; the rest shuffle on every visit so the
// people shots and the scenery shots stay mixed together.
function shuffleRest(photos) {
  const [first, ...rest] = photos;
  for (let i = rest.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [rest[i], rest[j]] = [rest[j], rest[i]];
  }
  return [first, ...rest];
}

function useMasonry(photos) {
  const containerRef = useRef(null);
  const [layout, setLayout] = useState({ positions: [], height: 0 });

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const compute = () => {
      const width = el.clientWidth;
      const cols = getColumnCount(width);
      const gap = width < 640 ? 8 : GAP;
      const colWidth = (width - gap * (cols - 1)) / cols;
      const heights = new Array(cols).fill(0);
      const positions = photos.map((p) => {
        const h = colWidth * (p.h / p.w);
        let shortest = 0;
        for (let i = 1; i < cols; i++) {
          if (heights[i] < heights[shortest]) shortest = i;
        }
        const x = shortest * (colWidth + gap);
        const y = heights[shortest];
        heights[shortest] = y + h + gap;
        return { x, y, w: colWidth, h };
      });
      setLayout({ positions, height: Math.max(0, ...heights) - gap });
    };

    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [photos]);

  return { containerRef, ...layout };
}

function GalleryItem({ photo, index, position, onClick }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisible(true);
            io.unobserve(e.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const fromRot = `${(index % 2 === 0 ? -1 : 1) * (4 + (index * 7) % 5)}deg`;
  const delay = (index % 5) * 70;

  return (
    <div
      ref={ref}
      className="gallery-cell"
      style={{
        width: position ? position.w : 0,
        height: position ? position.h : 0,
        transform: position
          ? `translate3d(${position.x}px, ${position.y}px, 0)`
          : undefined,
      }}
    >
      <figure
        className={`gallery-item ${visible ? 'is-visible' : ''}`}
        style={{
          '--from-rot': fromRot,
          '--delay': `${delay}ms`,
        }}
        onClick={() => onClick(index)}
      >
        <img
          src={photo.src}
          alt=""
          loading="lazy"
          decoding="async"
          width={photo.w}
          height={photo.h}
        />
      </figure>
    </div>
  );
}

function Lightbox({ photos, index, onClose, onPrev, onNext }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') onPrev();
      else if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, onPrev, onNext]);

  const photo = photos[index];

  return (
    <div className="lightbox" onClick={onClose}>
      <button className="lightbox-close" onClick={onClose} aria-label="Close">
        <FaTimes />
      </button>
      <button
        className="lightbox-nav lightbox-prev"
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        aria-label="Previous"
      >
        <FaChevronLeft />
      </button>
      <img
        key={photo.src}
        src={photo.src}
        alt=""
        className="lightbox-image"
        onClick={(e) => e.stopPropagation()}
      />
      <button
        className="lightbox-nav lightbox-next"
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        aria-label="Next"
      >
        <FaChevronRight />
      </button>
      <div className="lightbox-counter">{index + 1} / {photos.length}</div>
    </div>
  );
}

export default function Gallery({ title, subtitle }) {
  const [activeIndex, setActiveIndex] = useState(null);
  const photos = useMemo(() => shuffleRest(gallery), []);
  const { containerRef, positions, height } = useMasonry(photos);

  const open = useCallback((i) => setActiveIndex(i), []);
  const close = useCallback(() => setActiveIndex(null), []);
  const prev = useCallback(() => {
    setActiveIndex((i) => (i === null ? null : (i - 1 + photos.length) % photos.length));
  }, [photos.length]);
  const next = useCallback(() => {
    setActiveIndex((i) => (i === null ? null : (i + 1) % photos.length));
  }, [photos.length]);

  return (
    <section className="gallery-section">
      {(title || subtitle) && (
        <div className="gallery-header">
          {title && <h2 className="gallery-title">{title}</h2>}
          {subtitle && <p className="gallery-subtitle">{subtitle}</p>}
        </div>
      )}
      <div
        ref={containerRef}
        className="gallery-grid"
        style={{ height: height ? `${height}px` : undefined }}
      >
        {photos.map((photo, i) => (
          <GalleryItem
            key={photo.src}
            photo={photo}
            index={i}
            position={positions[i]}
            onClick={open}
          />
        ))}
      </div>
      {activeIndex !== null && (
        <Lightbox photos={photos} index={activeIndex} onClose={close} onPrev={prev} onNext={next} />
      )}
    </section>
  );
}
